'use client'

import { LucideIcon } from 'lucide-react'
import Link from 'next/link'

interface StatCardProps {
  icon: LucideIcon
  label: string
  value: number
  href?: string
  highlight?: boolean
  color?: 'blue' | 'amber' | 'green' | 'red' | 'purple' | 'light-blue' | 'dark-blue'
  subLabel?: string
  zeroLabel?: string
  miniCtaLabel?: string
}

const colorClasses = {
  blue: 'bg-white text-gray-900 border-gray-200',
  'light-blue': 'bg-white text-gray-900 border-gray-200',
  'dark-blue': 'bg-white text-gray-900 border-gray-200',
  amber: 'bg-white text-gray-900 border-gray-200',
  green: 'bg-white text-gray-900 border-gray-200',
  red: 'bg-white text-gray-900 border-gray-200',
  purple: 'bg-white text-gray-900 border-gray-200',
}

export function StatCard({ icon: Icon, label, value, href, highlight, color = 'blue', subLabel, zeroLabel, miniCtaLabel }: StatCardProps) {
  const baseClasses = `flex flex-col gap-2 p-5 rounded-xl border transition-all ${
    highlight ? 'border-blue-500 bg-blue-50' : ''
  } ${colorClasses[color]} ${href ? 'hover:shadow-md hover:border-gray-300' : ''}`

  const content = (
    <>
      <div className="flex items-center justify-between">
        <Icon className={`w-5 h-5 ${highlight ? 'text-blue-600' : 'text-gray-600'}`} />
        {highlight && (
          <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded-full">NEW</span>
        )}
      </div>
      <div className="space-y-1">
        <p className={`text-2xl font-bold ${highlight ? 'text-blue-600' : 'text-gray-900'}`}>
          {value === 0 && zeroLabel ? zeroLabel : value}
        </p>
        <p className="text-sm font-medium text-gray-600">{label}</p>
        {subLabel && <p className="text-xs text-gray-500">{subLabel}</p>}
        {miniCtaLabel && <p className="text-xs font-semibold text-blue-600">{miniCtaLabel} →</p>}
      </div>
    </>
  )

  if (href) {
    return (
      <Link href={href} className={baseClasses + ' hover:shadow-md cursor-pointer'}>
        {content}
      </Link>
    )
  }

  return <div className={baseClasses}>{content}</div>
}
